import React, { useState } from 'react';
import axios from 'axios';
import { FaTimes, FaCreditCard, FaWhatsapp, FaCheckCircle } from 'react-icons/fa';
import '../styles/Checkout.css';

const Checkout = ({ isOpen, onClose, cartItems, onOrderSuccess }) => {
  const [formData, setFormData] = useState({
    name: '',
    phone: '',
    email: '',
    address: '',
    city: '',
    notes: ''
  });

  const [paymentMethod, setPaymentMethod] = useState('cod');
  const [loading, setLoading] = useState(false);
  const [orderPlaced, setOrderPlaced] = useState(false);
  const [orderDetails, setOrderDetails] = useState(null);

  const calculateTotal = () => {
    return cartItems.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const handleInputChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    if (paymentMethod === 'whatsapp') {
      handleWhatsAppOrder();
      return;
    }

    setLoading(true);

    try {
      const orderData = {
        customerInfo: formData,
        items: cartItems,
        paymentMethod,
        totalAmount: calculateTotal()
      };

      const response = await axios.post('/api/orders', orderData);

      if (response.data.success) {
        setOrderDetails(response.data.order);
        setOrderPlaced(true);
        setTimeout(() => {
          onOrderSuccess();
          resetForm();
        }, 3000);
      }
    } catch (error) {
      console.error('Error placing order:', error);
      alert('Failed to place order. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleWhatsAppOrder = () => {
    const orderSummary = cartItems.map(item =>
      `${item.name} x ${item.quantity} = Rs. ${item.price * item.quantity}`
    ).join('%0A');

    const message = `*New Order from Dairyzar*%0A%0A*Customer Details:*%0AName: ${formData.name}%0APhone: ${formData.phone}%0AEmail: ${formData.email}%0AAddress: ${formData.address}%0ACity: ${formData.city}%0A%0A*Order Items:*%0A${orderSummary}%0A%0A*Total Amount: Rs. ${calculateTotal()}*%0A%0ANotes: ${formData.notes}`;

    window.open(`https://wa.me/923126108161?text=${message}`, '_blank');

    setOrderPlaced(true);
    setTimeout(() => {
      onOrderSuccess();
      resetForm();
    }, 2000);
  };

  const resetForm = () => {
    setFormData({
      name: '',
      phone: '',
      email: '',
      address: '',
      city: '',
      notes: ''
    });
    setOrderPlaced(false);
    setOrderDetails(null);
    onClose();
  };

  if (!isOpen) return null;

  if (orderPlaced) {
    return (
      <>
        <div className="checkout-overlay" onClick={onClose}></div>
        <div className="checkout-modal open">
          <div className="success-message">
            <FaCheckCircle className="success-icon" />
            <h2>Order Placed Successfully!</h2>
            {paymentMethod === 'whatsapp' ? (
              <p>Your order has been sent via WhatsApp. Our team will contact you shortly.</p>
            ) : (
              <p>Thank you for your order! We'll contact you soon to confirm delivery details.</p>
            )}
            {orderDetails && (
              <div className="order-id">Order ID: {orderDetails.orderId}</div>
            )}
          </div>
        </div>
      </>
    );
  }

  return (
    <>
      <div className="checkout-overlay" onClick={onClose}></div>
      <div className={`checkout-modal ${isOpen ? 'open' : ''}`}>
        <div className="checkout-header">
          <h2>Checkout</h2>
          <button className="close-checkout" onClick={onClose}>
            <FaTimes />
          </button>
        </div>

        <div className="checkout-body">
          <div className="checkout-grid">
            <div className="checkout-form-section">
              <h3>Shipping Information</h3>
              <form onSubmit={handleSubmit}>
                <div className="form-group">
                  <label>Full Name *</label>
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your full name"
                  />
                </div>

                <div className="form-group">
                  <label>Phone Number *</label>
                  <input
                    type="tel"
                    name="phone"
                    value={formData.phone}
                    onChange={handleInputChange}
                    required
                    placeholder="03XX-XXXXXXX"
                  />
                </div>

                <div className="form-group">
                  <label>Email</label>
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleInputChange}
                    placeholder="your.email@example.com"
                  />
                </div>

                <div className="form-group">
                  <label>Complete Address *</label>
                  <textarea
                    name="address"
                    value={formData.address}
                    onChange={handleInputChange}
                    required
                    rows="3"
                    placeholder="House no, street, area"
                  ></textarea>
                </div>

                <div className="form-group">
                  <label>City *</label>
                  <input
                    type="text"
                    name="city"
                    value={formData.city}
                    onChange={handleInputChange}
                    required
                    placeholder="Enter your city"
                  />
                </div>

                <div className="form-group">
                  <label>Order Notes (Optional)</label>
                  <textarea
                    name="notes"
                    value={formData.notes}
                    onChange={handleInputChange}
                    rows="2"
                    placeholder="Any special instructions?"
                  ></textarea>
                </div>

                <div className="payment-methods">
                  <h3>Payment Method</h3>
                  <div className="payment-options">
                    <label className={`payment-option ${paymentMethod === 'cod' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="payment"
                        value="cod"
                        checked={paymentMethod === 'cod'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      />
                      <div className="payment-content">
                        <FaCreditCard className="payment-icon" />
                        <div>
                          <strong>Cash on Delivery</strong>
                          <p>Pay when you receive your order</p>
                        </div>
                      </div>
                    </label>

                    <label className={`payment-option ${paymentMethod === 'whatsapp' ? 'active' : ''}`}>
                      <input
                        type="radio"
                        name="payment"
                        value="whatsapp"
                        checked={paymentMethod === 'whatsapp'}
                        onChange={(e) => setPaymentMethod(e.target.value)}
                      />
                      <div className="payment-content">
                        <FaWhatsapp className="payment-icon whatsapp" />
                        <div>
                          <strong>Order via WhatsApp</strong>
                          <p>Complete order through WhatsApp chat</p>
                        </div>
                      </div>
                    </label>
                  </div>
                </div>

                <button type="submit" className="btn btn-primary submit-order-btn" disabled={loading}>
                  {loading ? 'Processing...' : paymentMethod === 'whatsapp' ? 'Order on WhatsApp' : 'Place Order'}
                </button>
              </form>
            </div>

            <div className="order-summary-section">
              <h3>Order Summary</h3>
              <div className="summary-items">
                {cartItems.map((item) => (
                  <div key={item.id} className="summary-item">
                    <img src={item.image} alt={item.name} />
                    <div className="summary-item-details">
                      <p className="summary-item-name">{item.name}</p>
                      <p className="summary-item-qty">Qty: {item.quantity}</p>
                    </div>
                    <p className="summary-item-price">Rs. {item.price * item.quantity}</p>
                  </div>
                ))}
              </div>

              <div className="summary-totals">
                <div className="summary-row">
                  <span>Subtotal:</span>
                  <span>Rs. {calculateTotal()}</span>
                </div>
                <div className="summary-row">
                  <span>Shipping:</span>
                  <span>{calculateTotal() >= 3000 ? 'FREE' : 'Rs. 200'}</span>
                </div>
                <div className="summary-row total">
                  <span>Total:</span>
                  <span>Rs. {calculateTotal() >= 3000 ? calculateTotal() : calculateTotal() + 200}</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default Checkout;